import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { ResetDataCard } from "@/components/ResetDataCard";
import { SampleDataCard } from "@/components/SampleDataCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, NativeSelect } from "@/components/common";
import { IntegrationsPanel } from "@/components/IntegrationsPanel";
import { api, errMsg } from "@/lib/api";

const FIELDS = [
  ["business_name", "Business name"], ["tagline", "Tagline"], ["phone", "Business phone / WhatsApp"], ["address", "Address"],
  ["gstin", "GSTIN (leave empty if not registered)"], ["state", "State (for CGST/SGST vs IGST)"],
  ["upi_id", "UPI ID for payments (e.g. yourname@okaxis)"], ["upi_name", "UPI payee name"], ["upi_qr_url", "UPI / PhonePe QR image URL (shown on customer bill page)"], ["invoice_prefix", "Invoice number prefix"],
  ["app_url", "App link used in WhatsApp messages"], ["owner_whatsapp", "Owner personal WhatsApp (alerts, daily summary, tests) - not the business number"],
  ["logo_url", "Logo image URL"], ["membership_fee", "Membership fee (₹)"],
  ["kitchen_maps_url", "Kitchen Google Maps link (route start)"],
  ["owner_email", "Owner email (monthly PDF report on the 1st)"],
  ["delivery_slots", "Delivery time slots (comma separated, in order)"],
  ["referral_reward", "Refer-a-friend reward to referrer (₹, 0 = off)"], ["referral_max", "Max rewarded referrals per customer (0 = no limit)"],
  ["winback_amount", "Win-back credit after a low rating (₹, 0 = off)"], ["birthday_treat", "Birthday treat credit (₹, 0 = wishes only)"],
  ["delivery_capacity", "Max deliveries per partner per morning"], ["plan_max_km", "Max km between societies combined for one partner"],
];

const TOGGLES = [
  ["auto_ask_enabled", "Auto-send evening order questions", "Daily at 6:00 PM IST"],
  ["weekly_bill_enabled", "Auto-send weekly bills with pay link", "Every Sunday at 10:00 AM IST"],
  ["reminder_enabled", "Auto-send overdue payment reminders", "Dues unpaid for more than 7 days, at 10:00 AM IST"],
  ["credit_alert_whatsapp", "WhatsApp me when a customer crosses the credit limit", "Also shown in the alerts bell"],
  ["welcome_enabled", "Send a WhatsApp welcome to new members", "With their personal order link, as soon as the membership is active"],
  ["confirm_enabled", "Send WhatsApp 'order confirmed' summary", "Delivery date, time slot, items and qty. Instantly on confirm/change, and at 8:00 PM IST for regular orders"],
  ["receipt_enabled", "Send WhatsApp payment receipt", "Instantly when you approve a customer's UTR payment"],
  ["load_enabled", "Send evening load list to delivery staff", "Daily 8:30 PM IST: tomorrow's order count and products to load (add their phone in Team)"],
  ["owner_summary_enabled", "Send me a daily summary on WhatsApp", "Daily 9:00 PM IST: tomorrow's orders & kitchen plan, today's collections, outstanding"],
  ["delivered_enabled", "Send customers a 'delivered' message", "Instantly after delivery, with time, items and a link to the photo proof"],
  ["winback_monthly", "Limit win-back offer to once per customer per month", "Otherwise every low rating (2★ or below) gets the win-back credit"],
  ["weekly_report_enabled", "Send me a weekly business report", "Every Sunday 9:00 PM IST: sales, est. profit, wastage, avg rating, new members, collections"],
  ["birthday_enabled", "Send birthday wishes", "Daily 8:00 AM IST to customers whose birthday is today, with the treat credit"],
  ["followup_enabled", "Send follow-up call list to salespeople", "Daily 9:00 AM IST: each salesperson gets the leads due for a call today (add their WhatsApp number in Team)"],
  ["winner_enabled", "Announce the weekly sales winner", "Every Monday 9:15 AM IST: last week's leaderboard winner to all salespeople and you"],
  ["new_lead_alert_enabled", "WhatsApp salespeople their new leads", "After each Google Sheet sync (9:00 AM IST or Sync now): names and phones of the leads just assigned to them"],
  ["auto_plan_enabled", "Auto-plan tomorrow's deliveries", "Daily 8:15 PM IST: group by society and tower, assign partners up to the max per partner"],
];

export default function SettingsPage() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setF({ ...r.data, app_url: r.data.app_url || window.location.origin })); }, []);
  const save = async () => {
    try { const { key, ...body } = f; setF(await api.put("/settings", body).then((r) => r.data)); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!f) return null;
  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Settings" title="Business profile" subtitle="Shown on invoices, customer pages and UPI payment links.">
        <Button asChild variant="outline" className="rounded-full border-[#0F382C]/30 text-[#0F382C]">
          <a href="/Nex_Fresh_KT_Guide.pdf" download="Nex_Fresh_KT_Guide.pdf" target="_blank" rel="noreferrer" data-testid="kt-guide-download-btn"><FileDown className="mr-2 h-4 w-4" />Download KT Guide (PDF)</a>
        </Button>
      </PageHeader>
      <div className="max-w-2xl rounded-3xl border border-[#E7E2D6] bg-white p-6 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([k, l]) => (
            <div key={k} className={`space-y-1 ${k === "address" || k === "app_url" ? "sm:col-span-2" : ""}`}>
              <Label>{l}</Label>
              <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}-input`} />
            </div>
          ))}
        </div>
        <div className="mt-5 space-y-3">
          {TOGGLES.map(([k, l, d]) => (
            <label key={k} className="flex items-center justify-between gap-3 rounded-xl border border-[#E7E2D6] p-4">
              <div><div className="font-semibold text-sm">{l}</div><div className="text-xs text-muted-foreground">{d} · via WhatsApp Business API (when connected)</div></div>
              <Switch checked={f[k] !== false} onCheckedChange={(v) => setF({ ...f, [k]: v })} data-testid={`settings-${k}-toggle`} />
            </label>
          ))}
          <div className="flex items-center justify-between gap-3 rounded-xl border border-[#E7E2D6] p-4">
            <div className="font-semibold text-sm">Reminder frequency</div>
            <NativeSelect className="w-56" value={f.reminder_frequency || "weekly"} onChange={(e) => setF({ ...f, reminder_frequency: e.target.value })} data-testid="settings-reminder-frequency">
              <option value="weekly">Every Wednesday 10 AM</option><option value="daily">Every day 10 AM</option>
            </NativeSelect>
          </div>
        </div>
        {!f.upi_id && <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="upi-warning">Add your UPI ID so customers get a one-tap "Pay via UPI" button.</div>}
        <Button className="mt-6 rounded-full bg-[#0F382C] hover:bg-[#134E3A]" onClick={save} data-testid="settings-save-btn">Save settings</Button>
      </div>
      <IntegrationsPanel />
      <SampleDataCard />
      <ResetDataCard />
    </div>
  );
}
